import { Howl, Howler, type HowlOptions, type SoundSpriteDefinitions } from 'howler'

/** Named keys you’ll trigger in your app. Add more as you like. */
export type SoundKey = 'load' | 'correct' | 'incorrect' | string

/** Flexible way to register sounds, aligned to Howler's types. */
export type SrcConfig =
  | string
  | {
      src: string | string[]
      volume?: number // 0.0 – 1.0
      rate?: number // default 1.0
      loop?: boolean // default false
      html5?: boolean // default false
      sprite?: SoundSpriteDefinitions
      onload?: () => void
      onloaderror?: (id: number, error: unknown) => void
    }

type HowlMap = Map<SoundKey, Howl>

/** Keeps src we registered for each key to avoid touching private Howl fields. */
const primarySrcByKey = new Map<SoundKey, string>()
const missingLogged = new Set<SoundKey>()

export class SoundManager {
  private sounds: HowlMap = new Map()
  /** Keys that should not be stopped by interrupting sounds (e.g., background music). */
  private nonInterruptibleKeys: Set<SoundKey> = new Set(['bgm'])

  /** Register (and preload) one or many sounds. Safe to call repeatedly. */
  register(entries: Record<SoundKey, SrcConfig | null>): void {
    ;(Object.keys(entries) as SoundKey[]).forEach(key => {
      if (!key) return
      const cfg = entries[key]
      if (!cfg) return
      let nextSrcArr: string[] = []

      if (typeof cfg === 'string') {
        if (cfg) nextSrcArr = [cfg]
      } else if (cfg.src) {
        if (Array.isArray(cfg.src)) {
          nextSrcArr = cfg.src.filter(Boolean)
        } else if (cfg.src) {
          nextSrcArr = [cfg.src]
        }
      }

      if (nextSrcArr.length === 0) {
        console.warn(`[audio] Skipping registration for key "${key}" (no valid src)`)
        return
      }
      const nextPrimary = nextSrcArr[0]

      const currentPrimary = primarySrcByKey.get(key)
      if (currentPrimary && currentPrimary === nextPrimary) {
        // Same source already registered; update mutable props (volume/rate/loop) if provided
        const existing = this.sounds.get(key)
        if (existing && typeof cfg !== 'string') {
          if (typeof cfg.volume === 'number') existing.volume(cfg.volume)
          if (typeof cfg.rate === 'number') existing.rate(cfg.rate)
          if (typeof cfg.loop === 'boolean') existing.loop(cfg.loop)
        }
        return
      }
      const existing = this.sounds.get(key)
      if (existing) {
        existing.unload()
        this.sounds.delete(key)
      }

      const base: HowlOptions = { src: nextSrcArr }
      const opts: HowlOptions =
        typeof cfg === 'string'
          ? base
          : {
              ...base,
              volume: cfg.volume ?? 1,
              rate: cfg.rate ?? 1,
              loop: cfg.loop ?? false,
              html5: cfg.html5 ?? false,
              sprite: cfg.sprite,
              onload: cfg.onload,
              onloaderror: cfg.onloaderror,
            }

      const howl = new Howl(opts)
      this.sounds.set(key, howl)
      primarySrcByKey.set(key, nextPrimary)
    })
  }

  /**
   * Play a cue by key.
   * @returns Howler sound id if started, otherwise undefined.
   */
  play(
    key: SoundKey,
    options?: {
      interrupt?: boolean
      volume?: number
      rate?: number
      spriteName?: string
      onComplete?: (id: number) => void
    }
  ): number | undefined {
    const howl = this.sounds.get(key)
    if (!howl) {
      if (!missingLogged.has(key)) {
        console.warn(`[audio] Tried to play unregistered key: "${key}"`)
        missingLogged.add(key)
      }
      return undefined
    }

    if (options?.interrupt) {
      this.stopAllExcept(this.nonInterruptibleKeys)
    }
    if (typeof options?.volume === 'number') howl.volume(options.volume)
    if (typeof options?.rate === 'number') howl.rate(options.rate)

    try {
      const id = options?.spriteName !== undefined ? howl.play(options.spriteName) : howl.play()
      // Ensure per-sound volume is applied to the specific playback instance
      if (typeof id === 'number' && typeof options?.volume === 'number') {
        try {
          howl.volume(options.volume, id)
        } catch {
          // ignore
        }
      }
      if (typeof id === 'number' && options?.onComplete) {
        howl.once('end', options.onComplete, id)
      }
      return typeof id === 'number' ? id : undefined
    } catch {
      return undefined
    }
  }

  onComplete(key: SoundKey, handler: (id: number) => void): void {
    const h = this.sounds.get(key)
    if (!h) {
      console.warn(`[audio] onComplete: "${key}" not registered yet`)
      return
    }
    h.on('end', handler)
  }

  offComplete(key: SoundKey, handler: (id: number) => void): void {
    const h = this.sounds.get(key)
    if (!h) return
    h.off('end', handler)
  }

  stop(key?: SoundKey): void {
    if (key) {
      const h = this.sounds.get(key)
      if (h) h.stop()
      return
    }
    this.sounds.forEach(h => h.stop())
  }

  /** Stop all registered sounds except the provided keys. */
  private stopAllExcept(excepts: Set<SoundKey>): void {
    this.sounds.forEach((h, k) => {
      if (!excepts.has(k)) h.stop()
    })
  }

  unload(key?: SoundKey): void {
    if (key) {
      const h = this.sounds.get(key)
      if (h) {
        h.unload()
        this.sounds.delete(key)
        primarySrcByKey.delete(key)
      }
      return
    }
    this.sounds.forEach(h => h.unload())
    this.sounds.clear()
    primarySrcByKey.clear()
  }

  mute(muted: boolean): void {
    Howler.mute(muted)
  }

  setMasterVolume(volume: number): void {
    Howler.volume(volume)
  }

  isPlaying(key: SoundKey): boolean {
    const h = this.sounds.get(key)
    return !!h && h.playing()
  }
}

/** Singleton */
export const audio = new SoundManager()
